import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  AcademicCapIcon, 
  FolderIcon 
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

// Dummy data for charts
const userGrowthData = [
  { month: 'Jan', users: 400 },
  { month: 'Feb', users: 300 },
  { month: 'Mar', users: 200 },
  { month: 'Apr', users: 278 },
  { month: 'May', users: 189 },
  { month: 'Jun', users: 239 }
];

const categoryDistributionData = [
  { name: 'Strength', value: 400 },
  { name: 'Cardio', value: 300 },
  { name: 'Flexibility', value: 200 },
  { name: 'Nutrition', value: 278 }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const levelProgressData = [
  { level: 'Beginner', count: 400 },
  { level: 'Intermediate', count: 300 },
  { level: 'Advanced', count: 200 },
  { level: 'Expert', count: 100 }
];

const Dashboard = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`
      space-y-6 sm:space-y-8 
      ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white'}
      min-h-screen p-4 sm:p-6 md:p-8
      transition-colors duration-300
    `}>
      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[
          { 
            icon: UsersIcon, 
            iconColor: 'text-blue-500', 
            title: 'Total Users', 
            value: '1,234' 
          },
          { 
            icon: DocumentTextIcon, 
            iconColor: 'text-green-500', 
            title: 'Total Workouts', 
            value: '456' 
          },
          { 
            icon: AcademicCapIcon, 
            iconColor: 'text-purple-500', 
            title: 'Total Levels', 
            value: '5' 
          },
          { 
            icon: FolderIcon, 
            iconColor: 'text-red-500', 
            title: 'Total Categories', 
            value: '12' 
          }
        ].map((card, index) => (
          <div 
            key={index} 
            className={`
              ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
              shadow-lg rounded-xl p-6 
              flex items-center space-x-4
              border
              transition-colors duration-300
            `}
          >
            <card.icon className={`h-12 w-12 ${card.iconColor}`} />
            <div>
              <h3 className={`
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} 
                text-sm
              `}>
                {card.title}
              </h3>
              <p className={`
                text-2xl font-bold 
                ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}
              `}>
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-8">
        {/* User Growth Chart */}
        <div className={`
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
          shadow-lg rounded-xl p-6
          border
          transition-colors duration-300
        `}>
          <h2 className={`
            text-xl font-semibold 
            ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} 
            mb-4
          `}>
            User Growth
          </h2>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={userGrowthData}
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={isDarkMode ? '#374151' : '#e5e7eb'} 
            />
            <XAxis 
              dataKey="month" 
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
            />
            <YAxis 
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                color: isDarkMode ? '#f3f4f6' : '#000'
              }}
            />
            <Legend 
              wrapperStyle={{ 
                color: isDarkMode ? '#f3f4f6' : '#000' 
              }} 
            />
            <Line type="monotone" dataKey="users" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution Chart */}
        <div className={`
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
          shadow-lg rounded-xl p-6
          border
          transition-colors duration-300
        `}>
          <h2 className={`
            text-xl font-semibold 
            ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} 
            mb-4
          `}>
            Category Distribution
          </h2>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
            <Pie
              data={categoryDistributionData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {categoryDistributionData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{
                backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                color: isDarkMode ? '#f3f4f6' : '#000'
              }}
            />
            <Legend 
              wrapperStyle={{ 
                color: isDarkMode ? '#f3f4f6' : '#000' 
              }} 
            />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Level Progress Chart */}
        <div className={`
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
          shadow-lg rounded-xl p-6
          border
          transition-colors duration-300
        `}>
          <h2 className={`
            text-xl font-semibold 
            ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} 
            mb-4
          `}>
            Level Progress
          </h2>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={levelProgressData}
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={isDarkMode ? '#374151' : '#e5e7eb'} 
            />
            <XAxis 
              dataKey="level" 
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
            />
            <YAxis 
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                color: isDarkMode ? '#f3f4f6' : '#000'
              }}
            />
            <Legend 
              wrapperStyle={{ 
                color: isDarkMode ? '#f3f4f6' : '#000' 
              }} 
            />
            <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`
          ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
          shadow-lg rounded-xl p-6
          border
          transition-colors duration-300
        `}>
          <h2 className={`
            text-xl font-semibold 
            ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} 
            mb-4
          `}>
            Recent Activity
          </h2>
          <ul className="space-y-4">
            {[
              { icon: UsersIcon, text: 'New user John Doe joined', time: '2 mins ago' },
              { icon: DocumentTextIcon, text: 'Workout plan created', time: '1 hour ago' },
              { icon: AcademicCapIcon, text: 'Level 2 completed', time: '3 hours ago' },
              { icon: FolderIcon, text: 'New category added', time: '5 hours ago' }
            ].map((activity, index) => (
              <li 
                key={index} 
                className="flex items-center justify-between"
              >
                <div className="flex items-center space-x-4">
                  <activity.icon className={`h-6 w-6 ${
                    index === 0 ? 'text-blue-500' : 
                    index === 1 ? 'text-green-500' : 
                    index === 2 ? 'text-purple-500' : 
                    'text-red-500'
                  }`} />
                  <span className={`
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    {activity.text}
                  </span>
                </div>
                <span className={`
                  text-sm 
                  ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}
                `}>
                  {activity.time}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
